export type StopOptionSource = {
  type: 'loading' | 'unloading' | 'transfer';
  parentLoadingPosition?: number | null;
  pointType?: 'warehouse' | 'application' | 'consignee' | null;
  pointId?: number | null;
  address?: string | null;
};

export type UnloadingOptionContext = {
  stops: StopOptionSource[];
  currentLoadingPosition: number;
  mainLoadingPos: number;
  mainUnloadingPos: number;
  mainUnloading: {
    pointType?: string | null;
    pointId?: number | null;
  };
  resolveWarehouseLabel?: (id: number) => string;
  resolveApplicationLabel?: (id: number) => string;
  resolveConsigneeLabel?: (id: number) => string;
};

export type ApplicationRef = {
  id: number;
  internalNumber?: string | null;
  unloadingAddress?: string | null;
  client?: { name?: string | null } | null;
  consignee?: { name?: string | null } | null;
};

export type CargoApplicationRef = {
  id: number;
  applicationId?: number | null;
  application?: { id?: number | null } | null;
};

export function buildUnloadingStopOptions(ctx: UnloadingOptionContext): Array<{ value: number; label: string }> {
  const options: Array<{ value: number; label: string }> = [];
  const {
    stops,
    currentLoadingPosition,
    mainLoadingPos,
    mainUnloadingPos,
    mainUnloading,
    resolveWarehouseLabel,
    resolveApplicationLabel,
    resolveConsigneeLabel
  } = ctx;

  if (currentLoadingPosition === mainLoadingPos) {
    let mainLabel = 'Основная выгрузка';
    if (mainUnloading.pointType === 'warehouse' && mainUnloading.pointId != null) {
      mainLabel = `Основная выгрузка: ${resolveWarehouseLabel?.(Number(mainUnloading.pointId)) ?? 'Склад'}`;
    } else if (mainUnloading.pointType === 'application' && mainUnloading.pointId != null) {
      mainLabel = `Основная выгрузка: ${resolveApplicationLabel?.(Number(mainUnloading.pointId)) ?? 'Заявка доставки'}`;
    } else if (mainUnloading.pointType === 'consignee' && mainUnloading.pointId != null) {
      mainLabel = `Основная выгрузка: ${resolveConsigneeLabel?.(Number(mainUnloading.pointId)) ?? 'Получатель'}`;
    }
    options.push({ value: mainUnloadingPos, label: mainLabel });
  }

  stops.forEach((stop, idx) => {
    if (stop.type !== 'unloading') return;
    if (Number(stop.parentLoadingPosition ?? mainLoadingPos) !== currentLoadingPosition) return;

    let desc = 'Выгрузка';
    if (stop.pointType === 'warehouse' && stop.pointId != null) {
      desc = resolveWarehouseLabel?.(Number(stop.pointId)) ?? 'Склад';
    } else if (stop.pointType === 'application' && stop.pointId != null) {
      desc = resolveApplicationLabel?.(Number(stop.pointId)) ?? 'Заявка доставки';
    } else if (stop.pointType === 'consignee' && stop.pointId != null) {
      desc = resolveConsigneeLabel?.(Number(stop.pointId)) ?? 'Получатель';
    } else if (stop.address) {
      desc = String(stop.address);
    }
    options.push({ value: idx + 1, label: `Стоп ${idx + 1}: ${desc}` });
  });

  return options;
}

function cargoBelongsToLoadingPosition(params: {
  fromDraftValue: string | number | undefined;
  loadingPosition: number;
  mainLoadingPos: number;
  positionByStopUid?: Map<string, number>;
}): boolean {
  const { fromDraftValue, loadingPosition, mainLoadingPos, positionByStopUid } = params;
  if (fromDraftValue === undefined) return loadingPosition === mainLoadingPos;
  if (typeof fromDraftValue === 'number') return fromDraftValue === loadingPosition;
  if (positionByStopUid) {
    const pos = positionByStopUid.get(fromDraftValue);
    return pos === loadingPosition;
  }
  return loadingPosition === mainLoadingPos;
}

export function resolveLoadingGroupApplicationIds(params: {
  loadingPosition: number;
  mainLoading: { pointType?: string | null; pointId?: number | null };
  stops: StopOptionSource[];
  cargoRows?: CargoApplicationRef[];
  fromStopDraft?: Record<number, string | number | undefined>;
  mainLoadingPos?: number;
  positionByStopUid?: Map<string, number>;
}): Set<number> {
  const ids = new Set<number>();
  const mainLoadingPos = params.mainLoadingPos ?? 0;
  const addId = (raw: unknown) => {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) ids.add(n);
  };

  if (params.loadingPosition === mainLoadingPos) {
    if (String(params.mainLoading.pointType || '').toLowerCase() === 'application') {
      addId(params.mainLoading.pointId);
    }
  } else {
    const stopIndex = params.loadingPosition - 1;
    const loadingStop = params.stops[stopIndex];
    if (loadingStop?.type === 'loading' && String(loadingStop.pointType || '').toLowerCase() === 'application') {
      addId(loadingStop.pointId);
    }
  }

  for (const cargo of params.cargoRows ?? []) {
    const cargoId = Number(cargo.id);
    const fromDraft = params.fromStopDraft?.[cargoId];
    if (
      !cargoBelongsToLoadingPosition({
        fromDraftValue: fromDraft,
        loadingPosition: params.loadingPosition,
        mainLoadingPos,
        positionByStopUid: params.positionByStopUid
      })
    ) {
      continue;
    }
    addId(cargo.applicationId ?? cargo.application?.id);
  }

  if (ids.size === 0 && params.loadingPosition === mainLoadingPos) {
    const pt = String(params.mainLoading.pointType || '').toLowerCase();
    if (pt === 'warehouse') {
      for (const cargo of params.cargoRows ?? []) {
        addId(cargo.applicationId ?? cargo.application?.id);
      }
    }
  }

  return ids;
}

export function filterApplicationsForLoadingGroup(
  applications: ApplicationRef[],
  allowedIds: Set<number>
): ApplicationRef[] {
  if (allowedIds.size === 0) return [];
  return applications.filter((app) => allowedIds.has(Number(app.id)));
}
